import type { ApolloClient } from '@apollo/client';
import { onError } from '@apollo/client/link/error';

import {
  isEveryGraphQLErrorForbidden,
  isPersistedQueryNotFoundError,
  reportApolloErrorToSentry,
} from './apolloErrorHelpers';
import {
  isTerminalAccountError,
  isUnauthorizedQueryError,
  logoutFromTerminalAccountError,
  recoverFromStaleSession,
} from './graphqlUnauthorizedRecovery';
import { AUTH_CALLBACK_PATHNAME } from './config';

export function createGraphqlErrorLink({
  getApolloClient,
  ignoreErrorsOnPathname,
}: {
  // Thunk because the link must be constructed before the ApolloClient that
  // owns it; it is only invoked once an operation errors, after the client
  // exists.
  getApolloClient: () => Pick<ApolloClient<unknown>, 'clearStore'>;
  ignoreErrorsOnPathname?: string;
}) {
  return onError(({ graphQLErrors, networkError, operation }) => {
    if (isPersistedQueryNotFoundError(graphQLErrors)) {
      window.location.reload();
      return;
    }

    if (window.location.pathname === AUTH_CALLBACK_PATHNAME) {
      return;
    }

    if (ignoreErrorsOnPathname && window.location.pathname === ignoreErrorsOnPathname) {
      return;
    }

    if (isTerminalAccountError({ graphQLErrors, networkError })) {
      logoutFromTerminalAccountError();
      return;
    }

    if (isUnauthorizedQueryError({ graphQLErrors, networkError })) {
      recoverFromStaleSession(getApolloClient());
      return;
    }

    // Don't report generic "Internal server error" messages to Sentry
    if (graphQLErrors?.every((error) => error.message === 'Internal server error')) {
      return;
    }

    // The API already reports authorization denials to Sentry
    if (isEveryGraphQLErrorForbidden(graphQLErrors)) {
      return;
    }

    if (graphQLErrors || networkError) {
      reportApolloErrorToSentry({ graphQLErrors, networkError, operation });
    }
  });
}
